import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowBack, ReceiptLongOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { searchCustomers } from "../../api/customerApi";
import { getAllGoods } from "../../api/goodsApi";
import { crmOpportunityApi } from "../../api/crmApi";
import NoteSelect from "../../components/NoteSelect";
import backgroundPattern from '../../assets/pasbookpng.png';
import "./SalesOrder.css";

const STORAGE_KEY = "salesOrders";
const today = () => new Date().toISOString().slice(0, 10);

const emptyItem = () => ({
  id: `${Date.now()}-${Math.random()}`,
  item: "",
  account: "Sales",
  qty: 1,
  rate: 0,
  discount: 0,
  disc: 0,
});

const safeArray = (v) => (Array.isArray(v)? v : []);
const money = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CustomerPicker({ value, onChange }) {
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState(null);
  const [localCustomers, setLocalCustomers] = useState([]);
  const customerName = (c) => c?.name || c?.displayName || c?.customer_name || c?.customerName || "";

  useEffect(() => {
    try { setLocalCustomers(safeArray(JSON.parse(localStorage.getItem("customers") || "[]"))); } catch { setLocalCustomers([]); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchCustomers({ name: query.trim() });
        const apiRows = Array.isArray(res?.data)? res.data : safeArray(res?.data?.data || res?.data?.customers);
        const q = query.trim().toLowerCase();
        const merged = [...apiRows,...localCustomers].filter(r => customerName(r).toLowerCase().includes(q));
        const seen = new Set();
        setCustomers(merged.filter(r => {
          const key = String(r.id?? customerName(r)).toLowerCase();
          if (seen.has(key)) return false; seen.add(key); return Boolean(customerName(r));
        }));
      } catch {
        const q = query.trim().toLowerCase();
        setCustomers(localCustomers.filter(r => customerName(r).toLowerCase().includes(q)));
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [open, query, localCustomers]);

  useEffect(() => {
    if (!open ||!triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, 320), window.innerWidth - 24);
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const showAbove = below < 260 && above > below;
      const maxHeight = Math.max(160, Math.min(300, showAbove? above : below));
      setStyle({ position: "fixed", left, top: showAbove? rect.top - maxHeight - 6 : rect.bottom + 6, width, maxHeight, zIndex: 10050 });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [open, customers.length]);

  useEffect(() => {
    const close = (e) => { if (triggerRef.current &&!triggerRef.current.contains(e.target) && dropdownRef.current &&!dropdownRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const list = open && style? (
    <div className="note-customer-dropdown" style={style} ref={dropdownRef}>
      <div className="note-customer-search"><input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search customer..." /></div>
      {loading && <div className="note-dropdown-state">Searching...</div>}
      {!loading && customers.length === 0 && <div className="note-dropdown-state">No customers found.</div>}
      {!loading && customers.map((c, i) => (
        <button type="button" className="note-customer-option" key={c.id?? `${customerName(c)}-${i}`} onClick={() => { onChange(customerName(c), c); setOpen(false); setQuery(""); }}>
          <strong>{customerName(c)}</strong><span>{c.contactNumber || c.phone || c.email || "Customer"}</span>
        </button>
      ))}
      <button type="button" className="note-add-customer" onClick={() => { setOpen(false); window.location.assign("/dashboard/customers/new"); }}>+ Add New Customer</button>
    </div>
  ) : null;

  return (<><div className="note-customer-picker" ref={triggerRef}><button type="button" className={`note-field note-customer-trigger ${value? "has-value" : ""}`} onClick={() => setOpen(o =>!o)}><span>{value || "Select or add a customer"}</span><span className="note-chevron">⌄</span></button></div>{typeof document!== "undefined" && createPortal(list, document.body)}</>);
}

export default function SalesOrder() {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData?.fullData || location.state?.editData || null;
  const quotationData = location.state?.quotationData || null;
  const crmOpportunity = location.state?.opportunity || null;
  const crmOpportunityId = location.state?.crmOpportunityId ?? crmOpportunity?.id ?? null;

  const getSourceRef = () => {
    if (quotationData) return { type: 'quotation', id: quotationData.id, number: quotationData.quotationNo || `EST-${String(quotationData.id || '').slice(-6)}` };
    if (editData?.sourceRef) return editData.sourceRef;
    return null;
  };

  const sourceRef = getSourceRef();

  const [customer, setCustomer] = useState(editData?.customer || quotationData?.customer?.name || (crmOpportunity ? `${crmOpportunity.customerName || ''}${crmOpportunity.company ? ` (${crmOpportunity.company})` : ''}` : ""));
  const [customerId, setCustomerId] = useState(editData?.customerId || quotationData?.customerId || null);
  const [salesOrderNo, setSalesOrderNo] = useState(editData?.salesOrderNo || editData?.noteNo || `SO-${String(Date.now()).slice(-6)}`);
  const [refNo, setRefNo] = useState(editData?.refNo || editData?.referenceNo || quotationData?.quotationNo || "");
  const [date, setDate] = useState(editData?.date || editData?.deliveryDate || today());
  const [expectOrderDate, setExpectOrderDate] = useState(editData?.expectOrderDate || "");
  const [paymentTerms, setPaymentTerms] = useState(editData?.paymentTerms || "Due on Receipt");
  const [deliverMethod, setDeliverMethod] = useState(editData?.deliverMethod || editData?.deliveryMethod || "");
  const [salesperson, setSalesperson] = useState(editData?.salesperson || "");
  const [customerNotes, setCustomerNotes] = useState(editData?.customerNotes || editData?.notes || quotationData?.notes || "");
  const [terms, setTerms] = useState(editData?.terms || quotationData?.terms || "");
  const [attachments, setAttachments] = useState(safeArray(editData?.attachments));
  const [attachFiles, setAttachFiles] = useState(safeArray(editData?.attachments));
  const [taxType, setTaxType] = useState(editData?.taxType || "GST");
  const [tax, setTax] = useState(editData?.tax || "GST 18%");
  const [taxAmount, setTaxAmount] = useState(editData?.taxAmount ?? quotationData?.gstAmount ?? 0);
  const [adjustment, setAdjustment] = useState(editData?.adjustment ?? 0);

  const mapQuotationItems = () => {
    if (!quotationData?.cylinders?.length) return [emptyItem()];
    return quotationData.cylinders.map((row) => ({
      id: `${Date.now()}-${Math.random()}`,
      item: row.cylinderType || row.cylinderName || '',
      account: 'Sales',
      qty: Number(row.qty || 1),
      rate: Number(row.rate || 0),
      discount: 0,
      disc: 0,
    }));
  };

  const [items, setItems] = useState(safeArray(editData?.items).length ? editData.items.map(r => ({...r, discount: r.discount ?? r.disc ?? 0, disc: r.disc ?? r.discount ?? 0 })) : mapQuotationItems());
  const [goods, setGoods] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedGoods, setSelectedGoods] = useState([]);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { let a = true; getAllGoods().then(r => { if (!a) return; const rows = Array.isArray(r?.data)? r.data : safeArray(r?.data?.data || r?.data?.goods); setGoods(rows); }).catch(() => a && setGoods([])); return () => { a = false; }; }, []);
  const goodsOptions = useMemo(() => goods.map(r => ({ id: r.id?? r.goodsId?? r.itemId?? r.name, name: r.name || r.itemName || r.goodsName || r.description || "", rate: Number(r.rate?? r.sellingPrice?? r.price?? 0), account: r.accountName || r.account || "Sales" })).filter(r => r.name), [goods]);


 const totals = useMemo(() => {
  const subtotal = items.reduce((s, r) => s + Number(r.qty || 0) * Number(r.rate || 0) - Number(r.discount || 0), 0);
  const taxVal = Number(taxAmount || 0);
  const taxEffect = tax === "No Tax" ? 0 : (taxType === "TDS" ? -taxVal : taxVal);
  return { 
    subtotal, 
    total: Math.max(0, subtotal + taxEffect + Number(adjustment || 0))
  };
}, [items, taxAmount, taxType, tax, adjustment]);

  const showToast = (m, t = "success") => { setToast({ message: m, type: t }); setTimeout(() => setToast(null), 2600); };
  const updateItem = (id, f, v) => setItems(rows => rows.map(r => r.id === id? {...r, [f]: v, disc: f === "discount"? v : r.disc } : r));
  const addRow = () => setItems(r => [...r, emptyItem()]);
  const removeRow = (id) => { if (items.length === 1) { showToast("At least one item row is required.", "error"); return; } setItems(r => r.filter(x => x.id!== id)); };
  const addBulkItems = () => {
    const rows = selectedGoods.map(gid => { const s = goodsOptions.find(r => String(r.id) === String(gid)); return {...emptyItem(), item: s.name, rate: s.rate, account: s.account }; });
    if (rows.length) { setItems(c => [...c,...rows]); showToast(`${rows.length} item(s) added.`); }
    setSelectedGoods([]); setBulkOpen(false);
  };

  const save = (status) => {
    if (!customer.trim()) { showToast("Customer Name is required.", "error"); return; }
    if (!date) { showToast("Delivery Date is required.", "error"); return; }
    const validItems = items.filter(r => String(r.item || "").trim());
    if (!validItems.length) { showToast("Add at least one item.", "error"); return; }
    setSaving(true);
    let existing = []; try { existing = safeArray(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")); } catch { existing = []; }

    const payload = {
      id: editData?.id || Date.now(),
      type: 'Sales Order',
      customer, customerId,
      salesOrderNo, noteNo: salesOrderNo,
      refNo, referenceNo: refNo,
      date, deliveryDate: date,
      expectOrderDate, expectedShipmentDate: expectOrderDate,
      paymentTerms, deliverMethod, deliveryMethod: deliverMethod,
      salesperson, customerNotes, notes: customerNotes,
      terms,
      attachments: attachFiles.map(f => f instanceof File? { name: f.name, size: f.size, type: f.type } : f),
      attachFiles: attachFiles.map(f => f instanceof File? { name: f.name, size: f.size, type: f.type } : f),
      taxType, tax, taxAmount: Number(taxAmount || 0),
      adjustment: Number(adjustment || 0),
      items: validItems.map(r => ({...r, disc: r.discount })),
      subTotal: totals.subtotal, subtotal: totals.subtotal,
      total: totals.total, amount: totals.total,
      balance: totals.total,
      dueDate: expectOrderDate,
      deliveredQty: editData?.deliveredQty || 0,
      pendingQty: editData?.pendingQty ?? (totals.total > 0 ? validItems.reduce((s, r) => s + Number(r.qty || 0), 0) : 0),
      cancelledQty: editData?.cancelledQty || 0,
      invoicedQty: editData?.invoicedQty || 0,
      status: status === "draft"? "Draft" : "Confirmed",
      sourceRef: sourceRef,
      crmOpportunityId: crmOpportunityId || null,
      updatedAt: new Date().toISOString(),
    };

    const next = editData? existing.map(r => r.id === editData.id? payload : r) : [...existing, payload];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    if (crmOpportunityId) {
      crmOpportunityApi.update(crmOpportunityId, { salesOrderId: payload.id, salesOrderNo, stage: status === "draft" ? undefined : "Won" }).catch(() => {});
    }
    showToast(status === "draft"? "Saved as draft." : "Sales Order saved.");
    setTimeout(() => { setSaving(false); navigate("/dashboard/sales-orders"); }, 600);
  };

  return (
    <div className="note-page">
      <div className="note-background-watermark" style={{ backgroundImage: `url(${backgroundPattern})` }} />
      {toast && <div className={`note-toast ${toast.type}`}><span>{toast.type === "success"? "✓" : "!"}</span>{toast.message}</div>}

      <header className="note-header">
        <div className="note-header-left"><div className="note-page-icon"><ReceiptLongOutlined /></div><div className="note-page-title-wrap"><h1>{editData ? "Edit" : "New"} Sales Order</h1><p>{quotationData ? `Created from ${quotationData.quotationNo || 'Quotation'}` : 'Create sales order - same as Debit Note structure'}</p></div></div>
        <button type="button" className="note-header-back" onClick={() => navigate("/dashboard/sales-orders")}><ArrowBack /></button>
      </header>

      <main className="note-shell">
        <section className="note-card">
          <div className="note-section-head"><div><h2>Sales Order details</h2><p>All variables from SaleList preview</p></div><span className="note-required">* Required</span></div>
          <div className="note-form-grid">
            <label className="note-field-group"><span>Customer Name <b>*</b></span><CustomerPicker value={customer} onChange={(n, r) => { setCustomer(n); setCustomerId(r?.id?? null); }} /></label>
            <label className="note-field-group"><span>Sales Order# <b>*</b></span><input className="note-field" value={salesOrderNo} onChange={e => setSalesOrderNo(e.target.value)} placeholder="SO-00001" /></label>
            <label className="note-field-group"><span>Reference#</span><input className="note-field" value={refNo} onChange={e => setRefNo(e.target.value)} /></label>
            <label className="note-field-group"><span>Delivery Date <b>*</b></span><input type="date" className="note-field" value={date} onChange={e => setDate(e.target.value)} /></label>
            <label className="note-field-group"><span>Expected Shipment Date</span><input type="date" className="note-field" value={expectOrderDate} onChange={e => setExpectOrderDate(e.target.value)} /></label>
            <label className="note-field-group"><span>Payment Terms</span><NoteSelect value={paymentTerms} onChange={setPaymentTerms} placeholder="Select" options={[{ value: "Due on Receipt", label: "Due on Receipt" }, { value: "Net 15", label: "Net 15" }, { value: "Net 30", label: "Net 30" }, { value: "Due on Delivery", label: "Due on Delivery" }]} /></label>
            <label className="note-field-group"><span>Delivery Method</span><NoteSelect value={deliverMethod} onChange={setDeliverMethod} placeholder="Select" options={[{ value: "Courier", label: "Courier" }, { value: "Transport", label: "Transport" }, { value: "Hand Delivery", label: "Hand Delivery" }]} /></label>
            <label className="note-field-group"><span>Salesperson</span><NoteSelect value={salesperson} onChange={setSalesperson} placeholder="Select" options={[{ value: "1", label: "Salesperson 1" }, { value: "2", label: "Salesperson 2" }]} /></label>
          </div>
        </section>

        <section className="note-card note-items-card">
  <div className="note-section-head"><div><h2>Items & services</h2><p>Item, Qty, Rate, Discount, Amount</p></div><button type="button" className="note-link-btn" onClick={() => setBulkOpen(true)}>+ Add items in bulk</button></div>
  <div className="note-table-wrap">
    <table className="note-table"><thead><tr><th>ITEM DETAILS</th><th>ACCOUNT</th><th>QTY</th><th>RATE</th><th>DISCOUNT</th><th>AMOUNT</th><th /></tr></thead>
      <tbody>{items.map(row => { const amount = Number(row.qty || 0) * Number(row.rate || 0) - Number(row.discount || 0); return <tr key={row.id}><td><input className="note-table-input" value={row.item} onChange={e => updateItem(row.id, "item", e.target.value)} list="goods-SO" placeholder="Type or select" /><datalist id="goods-SO">{goodsOptions.map(o => <option key={o.id} value={o.name} />)}</datalist></td><td><NoteSelect className="note-table-input" value={row.account} onChange={v => updateItem(row.id, "account", v)} options={[{ value: "Sales", label: "Sales" }, { value: "Discount", label: "Discount" }, { value: "General Income", label: "General Income" }]} /></td><td><input className="note-table-input number no-spin" type="number" value={row.qty} onChange={e => updateItem(row.id, "qty", e.target.value)} onWheel={e=>e.target.blur()} /></td><td><input className="note-table-input number no-spin" type="number" value={row.rate} onChange={e => updateItem(row.id, "rate", e.target.value)} onWheel={e=>e.target.blur()} /></td><td><input className="note-table-input number no-spin" type="number" value={row.discount} onChange={e => updateItem(row.id, "discount", e.target.value)} onWheel={e=>e.target.blur()} /></td><td className="note-amount">{money(amount)}</td><td><button type="button" className="note-remove-btn" onClick={() => removeRow(row.id)}>×</button></td></tr>; })}</tbody></table>
  </div>
  <button type="button" className="note-add-row" onClick={addRow}>+ Add new row</button>

  <div className="note-lower-grid">
    <label className="note-field-group"><span>Customer Notes</span><textarea className="note-field note-textarea large" value={customerNotes} onChange={e => setCustomerNotes(e.target.value)} placeholder="Notes that will appear" /></label>
    <div className="note-total-card">
      <div className="note-total-row"><span>Sub Total</span><strong>₹ {money(totals.subtotal)}</strong></div>
      
      <div className="note-tax-controls">
        <div className="note-tax-types">
          {["GST", "TDS", "TCS"].map(t => (
            <label key={t} className={taxType===t?'active':''}>
              <input type="radio" checked={taxType === t} onChange={() => {
                setTaxType(t);
                const pct = parseInt(tax.replace(/[^0-9]/g,''))||0;
                if(pct){ setTaxAmount(((totals.subtotal * pct)/100).toFixed(2)); }
              }} />{t}
            </label>
          ))}
        </div>
        <NoteSelect className="note-tax-select" value={tax} onChange={v => {
          setTax(v);
          const pct = parseInt(v.replace(/[^0-9]/g,''))||0;
          if(pct>0){ setTaxAmount(((totals.subtotal * pct)/100).toFixed(2)); }
          else { setTaxAmount(0); }
        }} options={[{ value: "GST 18%", label: "GST 18%" }, { value: "GST 12%", label: "GST 12%" }, { value: "GST 5%", label: "GST 5%" }, { value: "TDS 10%", label: "TDS 10%" }, { value: "TCS 1%", label: "TCS 1%" }, { value: "No Tax", label: "No Tax" }]} />
        <input className="note-tax-input no-spin" type="number" value={taxAmount} onChange={e => setTaxAmount(e.target.value)} onWheel={e=>e.target.blur()} />
      </div>
      
      <label className="note-total-row note-adjustment"><span>Adjustment</span><input className="note-adjustment-input no-spin" type="number" value={adjustment} onChange={e => setAdjustment(e.target.value)} onWheel={e=>e.target.blur()} /></label>
      <div className="note-total-row note-grand-total"><span>Total</span><strong>₹ {money(totals.total)}</strong></div>
      
      {/* breakdown - shows if added or deducted */}
      {Number(taxAmount)>0 && (
        <div style={{fontSize:'0.7rem',color:'#64748B',textAlign:'right',marginTop:'4px'}}>
          {taxType} {tax} {taxType==='TDS' ? '-' : '+'} ₹{money(taxAmount)} {taxType==='TDS' ? 'deducted from' : 'added to'} Sub Total
        </div>
      )}
    </div>
  </div>
</section>

        {/* Terms & Attachments in same row */}
        <div className="note-bottom-row">
          <section className="note-card note-bottom-col">
            <div className="note-section-head compact"><div><h2>Terms & Conditions</h2></div></div>
            <textarea className="note-field note-textarea" value={terms} onChange={e => setTerms(e.target.value)} placeholder="Terms" style={{ minHeight: 110 }} />
          </section>

          <section className="note-card note-bottom-col">
            <div className="note-section-head compact"><div><h2>Attachments</h2></div></div>
            <div className="cn-attach-wrap">
              <label className="cn-upload-btn">
                <input type="file" multiple hidden accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx" onChange={e => {
                  const files = Array.from(e.target.files || []);
                  setAttachFiles(prev => [...prev,...files]);
                  setAttachments(prev => [...prev,...files]);
                }} />
                + Upload File
              </label>
              <div className="cn-attach-list">
                {attachFiles.map((f, i) => (
                  <div key={i} className="cn-attach-item">
                    <span className="cn-file-icon">📄</span>
                    <span className="cn-file-name">{f.name || f}</span>
                    <span className="cn-remove" onClick={() => { setAttachFiles(attachFiles.filter((_, idx) => idx!== i)); setAttachments(attachments.filter((_, idx) => idx!== i)); }}>×</span>
                  </div>
                ))}
                {attachFiles.length === 0 && <div className="cn-no-file">No files attached</div>}
              </div>
            </div>
          </section>
        </div>

        <footer className="note-actions">
          <button type="button" className="note-btn secondary" onClick={() => navigate("/dashboard/sales-orders")}>Cancel</button>
          <button type="button" className="note-btn secondary" onClick={() => save("draft")} disabled={saving}>{saving? "Saving..." : "Save as draft"}</button>
          <button type="button" className="note-btn primary" onClick={() => save("open")} disabled={saving}>{saving? "Saving..." : "Save & send"}</button>
        </footer>
      </main>

      {bulkOpen && <div className="note-modal-backdrop" onMouseDown={() => setBulkOpen(false)}><div className="note-modal" onMouseDown={e => e.stopPropagation()}><div className="note-modal-head"><div><h3>Add items in bulk</h3></div><button onClick={() => setBulkOpen(false)}>×</button></div><div className="note-bulk-list">{goodsOptions.map(o => <label className="note-bulk-option" key={o.id}><input type="checkbox" checked={selectedGoods.includes(String(o.id))} onChange={e => { const id = String(o.id); setSelectedGoods(c => e.target.checked? [...c, id] : c.filter(v => v!== id)); }} /><span><strong>{o.name}</strong><small>₹ {money(o.rate)}</small></span></label>)}</div><div className="note-modal-actions"><button className="note-btn secondary" onClick={() => setBulkOpen(false)}>Cancel</button><button className="note-btn primary" onClick={addBulkItems}>Add {selectedGoods.length} items</button></div></div></div>}
    </div>
  );
}